import { useEffect, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { Button, Dropdown, Modal, Radio } from 'components/core';
import { Styled } from './';
import Image from 'next/image';
import { useUpdateSubscriptionFrequencyAndQuantity } from 'reactQuery/mutations';
import { filterSubscriptionsBySku } from 'components/ProductTile/ProductTile.helper';
import { getIconByNotificationType } from 'components/core/Notification';
import { useI18n } from 'contexts/i18n';
import {
  buildSubscriptionFrequenciesByQuantity,
  getAvailableSubscriptionQuantities,
} from 'components/ProductDetails/ProductDetails.helper';
import { useSubscriptionProduct } from 'reactQuery/queries/subscriptions/useSubscriptionProducts';
import { translateFrequencyLabel } from 'components/ProductDetails/ProductDetails.helper';

const ChangeSubscriptionFrequencyModal = ({
  subscriptionId,
  sku,
  currentFrequency,
  currentQuantity,
  name,
  imageUrl,
  onClose,
  currentDiscount,
}: {
  subscriptionId: number;
  sku: string;
  currentFrequency: string;
  currentQuantity: number;
  name: string;
  imageUrl: string;
  onClose: () => void;
  currentDiscount: number;
}) => {
  const [selectedQuantity, setSelectedQuantity] = useState(0);
  const [selectedFrequencyId, setSelectedFrequencyId] = useState(0);

  const i18n = useI18n();
  const { data: subscriptionProducts } = useSubscriptionProduct();

  const productSubscriptionOptions = filterSubscriptionsBySku(
    sku,
    subscriptionProducts
  );

  const [availableQuantities, setAvailableQuantities] = useState(
    getAvailableSubscriptionQuantities(productSubscriptionOptions)
  );

  const [availableFrequencies, setAvailableFrequencies] = useState<
    { label: string; value: string }[]
  >([]);

  const onQuantityChange = (val: string) => {
    setSelectedQuantity(parseInt(val));
    const availableFrequenciesForQuantity =
      buildSubscriptionFrequenciesByQuantity(
        productSubscriptionOptions,
        parseInt(val)
      );
    setAvailableFrequencies(
      availableFrequenciesForQuantity.map(freq =>
        translateFrequencyLabel(freq, i18n)
      )
    );
    setSelectedFrequencyId(parseInt(availableFrequenciesForQuantity[0]?.value));
  };

  const updateSubscriptionMutation =
    useUpdateSubscriptionFrequencyAndQuantity();

  const handleUpdateSubscription = async () => {
    await updateSubscriptionMutation.mutateAsync({
      subscriptionId: subscriptionId,
      frequency: selectedFrequencyId,
      quantity: selectedQuantity,
    });
    onClose();
  };

  return (
    <Modal overlaysize="small">
      <Styled.Container>
        <Styled.ModalHeader>
          <Styled.Title>{i18n['Change subscription:']}</Styled.Title>
          <Styled.CloseButton onClick={onClose}>
            <MdClose size={24} />
          </Styled.CloseButton>
        </Styled.ModalHeader>
        <Styled.ModalBody>
          <Styled.Notification>
            <Styled.NotificationIcon>
              {getIconByNotificationType('info')}
            </Styled.NotificationIcon>
            <Styled.NotificationBody>
              {
                i18n[
                  'Any changes to your subscription will apply to your next shipment. For any questions, feel free to contact our support team.'
                ]
              }
            </Styled.NotificationBody>
          </Styled.Notification>
          <Styled.ProductDetailsWrapper>
            <Styled.ImageWrapper>
              <Image src={imageUrl} alt={name} width={80} height={80} />
            </Styled.ImageWrapper>
            <Styled.CurrentPlan>
              <Styled.DropdownWrapper>
                <Styled.CurrentPlanLabel>
                  {i18n['Your Current Plan']}
                </Styled.CurrentPlanLabel>
                <Styled.CurrentPlanText>
                  {currentQuantity} {i18n['units every'] ?? 'units every'}{' '}
                  {i18n[currentFrequency] ?? currentFrequency}({currentDiscount}
                  % {i18n['off']})
                </Styled.CurrentPlanText>
                <Styled.ProductName>{name}</Styled.ProductName>
              </Styled.DropdownWrapper>
            </Styled.CurrentPlan>
          </Styled.ProductDetailsWrapper>

          <Styled.DropdownWrapper>
            <Styled.DropdownLabel>
              {i18n['Change Shipment quantity']}:
            </Styled.DropdownLabel>
            <Dropdown
              options={availableQuantities}
              onChange={onQuantityChange}
              label={i18n['Select Quantity'] ?? 'Select Quantity'}
              size={'sm'}
              width="60%"
              noBorder
            />
          </Styled.DropdownWrapper>

          <Styled.DropdownWrapper>
            <Styled.DropdownLabel>
              {i18n['Change Shipment frequency']}
            </Styled.DropdownLabel>
            <Dropdown
              options={availableFrequencies}
              selectedValue={selectedFrequencyId.toString()}
              onChange={val => setSelectedFrequencyId(parseInt(val))}
              label={i18n['Select Frequency'] ?? 'Select Frequency'}
              size={'sm'}
              width="70%"
              noBorder
              isDisabled={availableFrequencies.length === 0}
            />
          </Styled.DropdownWrapper>
        </Styled.ModalBody>
        <Styled.ModalFooter>
          <Button size="md" variant="secondary" onClick={onClose}>
            {i18n['Cancel']}
          </Button>
          <Button
            size="md"
            variant="primary"
            onClick={handleUpdateSubscription}
            disabled={
              updateSubscriptionMutation.isLoading ||
              selectedFrequencyId === 0 ||
              selectedQuantity === 0
            }
            loading={updateSubscriptionMutation.isLoading}
          >
            {i18n['Confirm']}
          </Button>
        </Styled.ModalFooter>
      </Styled.Container>
    </Modal>
  );
};

export default ChangeSubscriptionFrequencyModal;
